import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ChannelRequest} from '../../_models/configurations/channel/ChannelRequest';
import {Observable} from 'rxjs';
import {ChannelResponse} from '../../_models/configurations/channel/ChannelResponse';
import {TypeChannelRequest} from '../../_models/configurations/channel/TypeChannelRequest';
import {TypeChannel} from '../../_models/configurations/channel/TypeChannel';
import {SlotsByLabel} from '../../_models/planification/Slot';
import {ScreenRequest} from '../../_models/planification/ScreenRequest';
import {SlotWithSequence} from "../../_models/planification/SlotWithSequence";
import {ChannelTypeResponse} from "../../_models/configurations/channel/ChannelTypeResponse";
import {ScreenOverResponse} from 'src/app/_models/planification/ScreenOverResponse';
import {ScreenOverRequest} from "../../_models/planification/ScreenOverRequest";
import {ChannelSearch2} from "../../_models/configurations/channel/ChannelSearch2";
import {ScreenGroupResponse} from "../../_models/planification/ScreenGroupResponse";
import {SlotGroupWithSequence} from "../../_models/planification/SlotGroupWithSequence";
import {CotationModeValues} from "../../_models/projets/cotation/CotationMode";
import {ScreenGroupOverRequest} from "../../_models/planification/ScreenGroupOverRequest";
import {ScreenGroupOverResponse} from "../../_models/planification/ScreenGroupOverResponse";
import {TrailerType} from "../../_models/planification/TrailerType";
import {ChannelLightResponse} from "../../_models/configurations/channel/ChannelLightResponse";
import {ScreenUnitResponse} from "../../_models/planification/ScreenUnitResponse";
import {CibleMarketingResponse} from "../../_models/projets/cotation/CibleMarketingResponse";
import {ChannelSearchRequest} from 'src/app/_models/configurations/channel/ChannelSearchRequest';
import {SearchChannelResponse} from 'src/app/_models/configurations/channel/SearchChannelResponse';
import {groupBy} from '../../shared/utils/array-utils';
import {ScreenProgramRequest} from "../../_models/planification/ScreenProgramRequest";
import {ScreenProgramResponse} from "../../_models/planification/ScreenProgramResponse";
import {EnvService} from './env.service';

@Injectable({ providedIn: 'root' })
export class ChannelService {
   public slotGroups: SlotsByLabel[] = [];
   private environment = inject(EnvService);
   private apiURL = this.environment?.readConfig()?.apiUrl;
   constructor(private http: HttpClient) {
   }

   adChannel(channelRequest: ChannelRequest): Observable<any> {
      return this.http.post<any>(`${this.apiURL}/channel/adChannel`, channelRequest, { withCredentials: true });
   }

   editAdChannel(channelRequest: ChannelRequest): Observable<any> {
      return this.http.post<any>(`${this.apiURL}/channel/editAdChannel`, channelRequest, { withCredentials: true });
   }

   editAdTypeChannel(typeChannelRequest: TypeChannelRequest): Observable<any> {
      return this.http.post<any>(`${this.apiURL}/channel/editAdTypeChannel`, typeChannelRequest, { withCredentials: true });
   }

   getAllChannel(): Observable<ChannelResponse[]> {
      return this.http.get<ChannelResponse[]>(`${this.apiURL}/channel/listChannels`);
   }

   getChannels(channelSearchRequest: ChannelSearchRequest): Observable<SearchChannelResponse> {
      return this.http.post<SearchChannelResponse>(`${this.apiURL}/channel/searchChannels`, channelSearchRequest, { withCredentials: true });
   }

   getAllChannelLight(): Observable<ChannelLightResponse[]> {
      return this.http.get<ChannelLightResponse[]>(`${this.apiURL}/channel/listChannelLight`);
   }

   getOpenChannelLight(): Observable<ChannelLightResponse[]> {
      return this.http.get<ChannelLightResponse[]>(`${this.apiURL}/v2/channel?status=OPEN`);
   }

   getAllMarketing(): Observable<CibleMarketingResponse[]> {
      return this.http.get<CibleMarketingResponse[]>(`${this.apiURL}/cotation/cible`);
   }

   getMarketingBySupport(typeSupport: string): Observable<CibleMarketingResponse[]> {
      return this.http.get<CibleMarketingResponse[]>(`${this.apiURL}/cotation/cible?typeSupport=${encodeURIComponent(typeSupport)}`);
   }

   getAllChannelType(): Observable<ChannelTypeResponse[]> {
      return this.http.get<ChannelTypeResponse[]>(`${this.apiURL}/channel/listChannelType`);
   }

   getCodeChannels(): Observable<string[]> {
      return this.http.get<string[]>(`${this.apiURL}/channel/listCodeChannels`);
   }

   getChannelsTypeByCotationMode(cotationMode: CotationModeValues): Observable<ChannelTypeResponse[]> {
      return this.http.get<ChannelTypeResponse[]>(`${this.apiURL}/channel/type/cotationMode/${cotationMode}`);
   }

   getOpenChannelsBySupport(support: string) {
      return this.http.get(`${this.apiURL}/channel/listOpenChannels/${support}`);
   }

   getOpenChannelsByIds(ids: string): Observable<ChannelResponse[]> {
      return this.http.get<ChannelResponse[]>(`${this.apiURL}/channel/channelsbyId/${ids}`);
   }

   // Ne plus utiliser, choisir channel/channelsBySupportAndType (getChannelBySupportAndType)
   getChannelsByType(type: string): Observable<ChannelResponse[]> {
      return this.http.get<ChannelResponse[]>(`${this.apiURL}/channel/channelsByType/${type}`);
   }

   getChannelBySupportAndType(channelSearch: ChannelSearch2): Observable<ChannelResponse[]> {
      return this.http.post<ChannelResponse[]>(`${this.apiURL}/channel/channelsBySupportAndType`, channelSearch);
   }

   getChannelsByTypes(typeChannel: TypeChannel): Observable<any> {
      return this.http.post(`${this.apiURL}/channel/channelsByType`, typeChannel, { withCredentials: true });
   }

   getSupportStatus(): Promise<Record<string, string>> {
      return this.http.get<Record<string, string>>(`${this.apiURL}/channel/supportStatus`).toPromise();
   }

   getOrderStatus(): Promise<Record<string, string>> {
      return this.http.get<Record<string, string>>(`${this.apiURL}/channel/orderStatus`).toPromise();
   }

   public getSlotsByProgramLabel(slots: { value: SlotWithSequence, selected: boolean }[]) {
      return Object.entries(
         groupBy(slots, slot => slot.value.label)
      ).map(([key, value]) => ({ program: key, slots: value }));
   }

   public getSlotGroupsByProgramLabel(slotGroups: { value: SlotGroupWithSequence, selected: boolean }[]) {
      return Object.entries(
         groupBy(slotGroups, slotGroup => slotGroup.value.label)
      ).map(([key, value]) => ({ program: key, slots: value }));
   }

   getSlotsOutOfRange(req: { startDate: Date | string, endDate: Date | string }) {
      return this.http.post<{
         slotsBeforeStartDate: SlotWithSequence[],
         slotsAfterEndDate: SlotWithSequence[]
      }>(`${this.apiURL}/screen/ecran/out`, req);
   }

   public getUnitSlots(screenRequest: ScreenRequest): Observable<ScreenUnitResponse> {
      return this.http.post<ScreenUnitResponse>(`${this.apiURL}/screen/ecran/unit`, screenRequest);
   }

   public getSlotGroups(screenRequest: ScreenRequest): Observable<ScreenGroupResponse> {
      return this.http.post<ScreenGroupResponse>(`${this.apiURL}/screen/ecran/group`, screenRequest);
   }

   public getSlotByProgramAndDates(screenProgramRequest: ScreenProgramRequest): Observable<ScreenProgramResponse> {
      return this.http.post<ScreenProgramResponse>(`${this.apiURL}/screen/ecran/program`, screenProgramRequest);
   }

   getSlotsGroupsByProgram(screenProgramRequest: ScreenProgramRequest): Observable<ScreenGroupResponse> {
      return this.http.post<ScreenGroupResponse>(`${this.apiURL}/screen/ecran/group/program`, screenProgramRequest);
   }

   getSlotUnitsByProgram(screenProgramRequest: ScreenProgramRequest): Observable<ScreenProgramResponse> {
      return this.http.post<ScreenProgramResponse>(`${this.apiURL}/screen/ecran/unit/program`, screenProgramRequest);
   }

   overrideSlot(screenOverRequest: ScreenOverRequest): Observable<ScreenOverResponse> {
      return this.http.post<ScreenOverResponse>(`${this.apiURL}/screen/ecran/over`, screenOverRequest);
   }

   updateSlotContexts(request: ScreenProgramRequest): Observable<ScreenOverResponse> {
      return this.http.post<ScreenOverResponse>(`${this.apiURL}/screen/ecran/context`, request);
   }

   overrideSlotGroup(screenGroupOverRequest: ScreenGroupOverRequest): Observable<ScreenGroupOverResponse> {
      return this.http.post<ScreenGroupOverResponse>(`${this.apiURL}/screen/ecran/group/over`, screenGroupOverRequest);
   }

   importFile(file: File, comment: string): Promise<string> {
      return new Promise<string>((resolve, reject) => {
         const formData: FormData = new FormData();
         formData.append('file', file);
         formData.append('comment', comment);
         this.http.post(`${this.apiURL}/screen/ecran/add`, formData, { responseType: 'text' })
         .toPromise()
         .then((response: any) => {
            resolve(response);
         })
         .catch((error: any) => {
            reject(error);
         });
      });
   }

   getAuthorizedTrailers(): Observable<TrailerType[]> {
      return this.http.get<TrailerType[]>(`${this.apiURL}/screen/trailerType/list`);
   }
   syncChannel(channelId: string) {
      return this.http.post(`${this.apiURL}/v2/mediapilot/synchroEcran`, { channelId });
   }

}

